import { AdminLoanerOrRentalProps } from './AdminLoanerOrRental.types';
import css from './AdminLoanerOrRental.module.scss';
import classNames from 'classnames';
import { CarRental } from '@mui/icons-material';
import { AppointmentLoanerOrRentalOption, LoanerOrRentalOption } from '../../../../../API/Enums';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentState } from '../../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../../context/Localization/localization';
import { GMBox } from '../../../../../controls/Box';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { GMButtonGroup } from '../../../../../controls/ButtonGroup';
import { Mustache } from '../../../../../controls/Heading/Mustache';
import { GMRoundedIcon, GMSizedIcon } from '../../../../../controls/Icon';
import { Spacer } from '../../../../../controls/Spacer/Spacer';
import { getDisplayName, getFullDisplayName } from '../../../../../helpers/Appointment';
import { useDisplayLocalizedVehicle } from '../../../../../helpers/displayVehicle';
import { useLoanerOrRentalLocalizationText } from '../../../../../helpers/LoanerOrRentalLocalizationText/useLoanerOrRentalLocalizationText';
import { formatAsPhoneNumber } from '../../../../../helpers/Number';
import { ScrollableBody } from '../../../../Layout/ScrollableBody';
import { KeyManagementHeader } from '../../../../../controls/KeyManagementHeader/KeyManagementHeader';
import { IAppointment } from '../../../../../API/GoMoto';
import { LocalizationType } from '../../../../../context/Localization/localization.types';

/**
 * This screen displays a series of buttons to allow the advisor storing the customer's key
 * to indicate what kind of vehicle they will be returning based on what the dealer has selected.
 *
 * This component can only display on Express kiosks.
 *
 * @param onProceed - {@link AdminLoanerOrRentalProps.onProceed onProceed} description.
 */
export const AdminLoanerOrRental: GoMotoComponent<AdminLoanerOrRentalProps> = ({
    onProceed,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { dealer: { loanerOrRentalOption } } = useKioskState();
    const appointment: IAppointment = useAppointmentState();
    const appointmentDispatch = useAppointmentDispatch();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const { headerText, instructionText } = useLoanerOrRentalLocalizationText({ loanerOrRentalOption: loanerOrRentalOption });

    const displayName: string = getDisplayName(appointment);
    const fullName: string = getFullDisplayName(appointment, false);
    const vehicle: string = displayLocalizedVehicle(appointment)
        .toString();

    return (
        <ScrollableBody
            heading={
                <KeyManagementHeader
                    primaryHeading={headerText}
                    secondaryHeading={
                        Localization.formatString(
                            Localization.WillCustomerBeReturning,
                            displayName,
                            instructionText ?? AppointmentLoanerOrRentalOption.None,
                        ) as string
                    }
                    leftAlignSecondaryHeading
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        loanerOrRentalOption === LoanerOrRentalOption.Loaners || loanerOrRentalOption === LoanerOrRentalOption.Both ?
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => {
                                        appointmentDispatch({
                                            type: AppointmentDispatchTypes.Update,
                                            payload: { ...appointment,
                                                returningLoanerOrRental: AppointmentLoanerOrRentalOption.Loaner },
                                        });

                                        onProceed();
                                    }}
                                    label={Localization.ReturningLoaner}
                                    kioskOptions={{
                                        square: true,
                                        squareOptions: {
                                            roundedIcon: (
                                                <CarRental className={css.carIcon} />
                                            ),
                                        },
                                    }}
                                    id={Localization.ReturningLoaner}
                                    ariaLabel={Localization.ReturningLoaner}
                                />
                            ) : null,
                        loanerOrRentalOption === LoanerOrRentalOption.Rentals || loanerOrRentalOption === LoanerOrRentalOption.Both ?
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => {
                                        appointmentDispatch({
                                            type: AppointmentDispatchTypes.Update,
                                            payload: { ...appointment,
                                                returningLoanerOrRental: AppointmentLoanerOrRentalOption.Rental },
                                        });

                                        onProceed();
                                    }}
                                    label={Localization.ReturningRental}
                                    kioskOptions={{
                                        square: true,
                                        squareOptions: {
                                            roundedIcon: (
                                                <CarRental className={css.carIcon} />
                                            ),
                                        },
                                    }}
                                    id={Localization.ReturningRental}
                                    ariaLabel={Localization.ReturningRental}
                                />
                            ) : null,
                        (
                            <CTAButton
                                styleType={'primary'}
                                onClick={() => {
                                    appointmentDispatch({
                                        type: AppointmentDispatchTypes.Update,
                                        payload: { ...appointment,
                                            returningLoanerOrRental: AppointmentLoanerOrRentalOption.None },
                                    });

                                    onProceed();
                                }}
                                label={Localization.NoReturn}
                                kioskOptions={{
                                    squareOptions: {
                                        roundedIcon: (
                                            <GMRoundedIcon themeType={'primary'} roundedBackgroundSize={'large'}>
                                                <GMSizedIcon iconType={'No'} iconSize={'custom'} />
                                            </GMRoundedIcon>
                                        ),
                                    },
                                    square: true,
                                }}
                                id={Localization.NoReturn}
                                ariaLabel={Localization.NoReturn}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
            topDivider
            hideSpacer
        >
            {/** // TODO: Commonize this as a control since it is used in more than one place */}
            <div className={css.customerInformation}>
                <GMBox color={'primary'}>
                    <div className={classNames(css.label, css.bold)}>{fullName}</div>
                    <div className={css.label}>{appointment.pickupEmail ? appointment.pickupEmail : appointment.email}</div>
                    {(appointment.pickupPhone || appointment.phone) ? (
                        <div className={css.label}>{formatAsPhoneNumber(appointment.pickupPhone ? appointment.pickupPhone : appointment.phone ?? '')}</div>
                    ) : null}
                    {vehicle.trim() !== '' ? (
                        <div className={css.label}>{vehicle}</div>
                    ) : null}
                </GMBox>
                <br />
                <Spacer />
                <div className={css.mustache}>
                    <Mustache />
                </div>
            </div>
        </ScrollableBody>
    );
};